<script setup>
    import { useArchivesNeighborhoodsStore } from '@/stores/archives-neighborhoods';

    const hoodStore = useArchivesNeighborhoodsStore();
    const emit = defineEmits(['updateSites', 'resetSites', 'updateFilters']);

    const route = useRoute();
    const isFramed = ref(route.path.includes('iframe'));

    const props = defineProps({
        sites: {
            type: Array,
            required: true,
            default: [],
        },
    });

    const applyActive = ref(false);

    const filterOptions = ref({
        yearsFilters: [],
        hoodFilters: '',
    });

    const sortBy = ref('alpha');

    watch(
        [filterOptions, sortBy],
        ([newfilterOptions, newsortBy], [prevfilterOptions, prevsortBy]) => {
            const emptyBoxes = Object.keys(filterOptions.value).every((k) => filterOptions.value[k].length === 0);
            const showingAll = sortBy.value === 'alpha';
            const emptyValues = emptyBoxes && showingAll;
            if (emptyValues) applyActive.value = false;
            else applyActive.value = true;
        },
        { deep: true }
    );

    const years = computed(() => {
        const uniqueYears = [...new Set(props.sites.flatMap((item) => item.fields['OHC Years of Participation']))];
        return uniqueYears.sort((a, b) => b - a);
    });

    const hoods = computed(() => {
        if (hoodStore.neighborhoodsData) {
            const uniqueHoods = [
                ...new Set(
                    hoodStore.neighborhoodsData.flatMap((item) => {
                        return { name: item.fields['Name'], value: item.id };
                    })
                ),
            ];
            return uniqueHoods;
        }
    });

    function applyFilters() {
        let filtered = unref(props.sites);
        applyActive.value = false;
        if (filtered?.length) {
            if (filterOptions.value.yearsFilters.length) {
                filtered = filtered.filter((el) => {
                    if (el.fields['OHC Years of Participation']) {
                        return filterOptions.value.yearsFilters.some((value) => el.fields['OHC Years of Participation'].includes(value));
                    }
                    return false;
                });
            }
            if (filterOptions.value.hoodFilters) {
                console.info('Filtering for', filterOptions.value.hoodFilters);
                filtered = filtered.filter((el) => {
                    if (el.fields['OHC Neighborhood']) {
                        return el.fields['OHC Neighborhood'].includes(filterOptions.value.hoodFilters);
                        // filterOptions.value.hoodFilters.some(value => el.fields['OHC Neighborhood'].includes(value))
                    }
                    return false;
                });
            }
        }
        filtersToQuery();
        updateSites(filtered);
        emit('updateFilters', toRaw(filterOptions.value));
    }

    function updateSites(val) {
        emit('updateSites', val);
    }

    function resetAll() {
        for (let key in filterOptions.value) {
            filterOptions.value[key] = Array.isArray(filterOptions.value[key]) ? [] : '';
        }
        console.info(filterOptions.value);
        //router.push({ query: {} })
        resetSites();
        filtersToQuery();
    }

    function resetSites() {
        emit('resetSites');
    }

    defineExpose({
        resetSites,
        updateSites,
    });

    function filtersToQuery() {
        const url = new URL(window.location);
        Object.entries(filterOptions.value)
            .filter((value) => value)
            .forEach((entry) => {
                if (entry[1] && entry[1].length !== 0) url.searchParams.set(entry[0], entry[1]);
                else url.searchParams.delete(entry[0]);
            });
        window.history.replaceState(history.state, '', url);
    }

    function querytoFilters() {
        let p = window.siteQuery;
        if (p) {
            sortBy.value = route?.query?.sortBy ? route.query.sortBy : 'alpha';
        }
        applyFilters();
    }
</script>

<template>
    <div class="sites-filters" :class="{ framed: isFramed }">
        <div class="sites-filters-inner">
            <div class="header">
                <button class="back-caret" @click.prevent="emit('close')" aria-label="Close">
                    <SvgsBackCaret />
                </button>                     
                <h2 class="subhead-2">Filter Archives</h2>
                <button class="reset small-heavy uppercase" @click="resetAll">Reset</button>
            </div>
            <label class="card-header" for="archives-hood-select">Neighborhood:</label>
            <div class="select form-field sort-by">
                <select id="archives-hood-select" v-model="filterOptions.hoodFilters">
                    <option value="">All Neighborhoods</option>
                    <option v-for="hood in hoods" :value="hood.value" v-html="hood.name"></option>
                </select>
                <SvgsSelectArrow class="select-arrow" />
            </div>
            <div class="checkboxes">
                <fieldset>
                    <legend>Year Participating</legend>
                    <div class="checkbox-wrapper" v-for="(year, index) in years" :key="year">
                        <template v-if="year">
                            <label>{{ year }}<input type="checkbox" v-model="filterOptions.yearsFilters" :value="year" /></label>
                        </template>
                    </div>
                </fieldset>
            </div>
            <div class="apply-button-wrapper">
                <div class="container hd-container">
                    <button class="apply-cancel-buttons apply-button" @click="applyFilters" :disabled="!applyActive" :class="{ active: applyActive }">Apply</button>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
    .sites-filters {

        height: 100%;
        overflow: hidden;
        overflow-y: scroll;
        padding: 16px;

        @media (min-width: $tablet) {
            padding: 64px 64px 0px 64px;
        }        

        .apply-button-wrapper {
            position: fixed;
            bottom: 0;
            left: 0;
            width: 100%;
            background: white;
            // Clear of the iPhone home indicator (viewport-fit=cover is on).
            padding: 10px 0 calc(10px + env(safe-area-inset-bottom));
            box-shadow: 0 -2px 6px rgba(0, 0, 0, 0.1);

            @media (min-width: $tablet) {
                padding: 20px 0 calc(20px + env(safe-area-inset-bottom));
            }
        }           

        .sites-filters-inner {
            padding-bottom: calc(20px + env(safe-area-inset-bottom));
        }
        .header {
            position: relative;
            text-align: center;
            margin-bottom: 34px;

            .reset {
                position: absolute;
                right: 0;
                top: 2px;
            }

            .back-caret {
                position: absolute;
                left: 0;
                top: -3px;
            }
        }

        .sort-by {
            margin-top: 16px;
        }

        .amenities {
            @media (min-width: $tablet) {
                margin-bottom: 60px;
            }
        }
    }
</style>
